import api from './api';
import EmptyState from './EmptyState';
import { getMonthName, getDayLettersForMonth } from './dashboardData';

import {
  Text,
  TextInput,
  Select,
  Table,
  Paper,
  Box,
  Flex,
  Group,
  Pagination,
  ActionIcon,
  Badge,
  Button,
} from '@mantine/core';

import {
  IconSearch,
  IconFilter,
  IconDownload,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';

import {
  useState,
  useMemo,
  useEffect,
} from 'react';

import * as XLSX from 'xlsx';

// ============================================================
// MESES
// ============================================================

const MESES = [
  { value: '1', label: 'ENERO' },
  { value: '2', label: 'FEBRERO' },
  { value: '3', label: 'MARZO' },
  { value: '4', label: 'ABRIL' },
  { value: '5', label: 'MAYO' },
  { value: '6', label: 'JUNIO' },
  { value: '7', label: 'JULIO' },
  { value: '8', label: 'AGOSTO' },
  { value: '9', label: 'SEPTIEMBRE' },
  { value: '10', label: 'OCTUBRE' },
  { value: '11', label: 'NOVIEMBRE' },
  { value: '12', label: 'DICIEMBRE' },
];

// ============================================================
// AÑOS
// ============================================================

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();

  return Array.from(
    { length: 7 },
    (_, i) => {
      const year = currentYear - 3 + i;

      return {
        value: String(year),
        label: String(year),
      };
    }
  );
};

export default function TdIti() {
  // ============================================================
  // PERIODO
  // ============================================================

  const now = new Date();

  const [mes, setMes] = useState(
    now.getMonth() + 1
  );

  const [anio, setAnio] = useState(
    now.getFullYear()
  );

  // ============================================================
  // CAMBIO DE MES
  // ============================================================

  const prevMonth = () => {
    if (mes === 1) {
      setMes(12);
      setAnio(anio - 1);
    } else {
      setMes(mes - 1);
    }

    setPage(1);
  };

  const nextMonth = () => {
    if (mes === 12) {
      setMes(1);
      setAnio(anio + 1);
    } else {
      setMes(mes + 1);
    }

    setPage(1);
  };

  // ============================================================
  // CAMBIO MANUAL DE MES
  // ============================================================

  const handleMonthChange = (value) => {
    if (!value) return;

    setMes(parseInt(value, 10));
    setPage(1);
  };

  // ============================================================
  // CAMBIO MANUAL DE AÑO
  // ============================================================

  const handleYearChange = (value) => {
    if (!value) return;

    setAnio(parseInt(value, 10));
    setPage(1);
  };

  // ============================================================
  // DÍAS DEL MES
  // ============================================================

  const DAYS = useMemo(() => {
    const totalDays = new Date(
      anio,
      mes,
      0
    ).getDate();

    return Array.from(
      { length: totalDays },
      (_, i) => i + 1
    );
  }, [mes, anio]);

  // ============================================================
  // LETRAS DE DÍAS
  // ============================================================

  const DAY_LETTERS = useMemo(() => {
    return getDayLettersForMonth(
      mes,
      anio
    );
  }, [mes, anio]);

  // ============================================================
  // DATA
  // ============================================================

  const [itiData, setItiData] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [apiError, setApiError] =
    useState(null);

  // ============================================================
  // CARGAR DATA
  // ============================================================

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setApiError(null);

      try {
        const response = await api.get(
          `/itinerario?mes=${mes}&anio=${anio}&limit=5000`
        );

        if (cancelled) return;

        const data =
          response.data?.data;

        console.log(
          'ITINERARIO RESPONSE:',
          response.data
        );

        if (Array.isArray(data)) {
          setItiData(data);
        } else {
          console.warn(
            'La API no regresó un arreglo en data:',
            response.data
          );

          setItiData([]);
        }
      } catch (error) {
        if (cancelled) return;

        console.error(
          'Error cargando itinerario:',
          error
        );

        setApiError(
          error.response?.data?.message ||
          error.message ||
          'Error al cargar el itinerario.'
        );

        setItiData([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [mes, anio]);

  // ============================================================
  // FILTROS
  // ============================================================

  const [search, setSearch] =
    useState('');

  const [rutaFilter, setRutaFilter] =
    useState(null);

  const [page, setPage] =
    useState(1);

  const [hoveredCol, setHoveredCol] = useState(null);

  const perPage = 20;

  // ============================================================
  // OPCIONES DE RUTA
  // ============================================================

  const rutaOptions = useMemo(() => {
    const rutas = [
      ...new Set(
        itiData
          .map((row) => row.Ruta)
          .filter(Boolean)
      ),
    ];

    return rutas
      .sort()
      .map((ruta) => ({
        value: ruta,
        label: ruta,
      }));
  }, [itiData]);

  // ============================================================
  // FILTRADO
  // ============================================================

  const filtered = useMemo(() => {
    let data = [...itiData];

    // ----------------------------------------------------------
    // BUSCAR
    // ----------------------------------------------------------

    if (search.trim()) {
      const s =
        search
          .trim()
          .toLowerCase();

      data = data.filter((row) => {
        const id = String(
          row.ID ?? ''
        ).toLowerCase();

        const ruta = String(
          row.Ruta ?? ''
        ).toLowerCase();

        const vuelo = String(
          row.Vuelo ?? ''
        ).toLowerCase();

        return (
          id.includes(s) ||
          ruta.includes(s) ||
          vuelo.includes(s)
        );
      });
    }

    // ----------------------------------------------------------
    // RUTA
    // ----------------------------------------------------------

    if (rutaFilter) {
      data = data.filter(
        (row) =>
          row.Ruta === rutaFilter
      );
    }

    return data;
  }, [
    itiData,
    search,
    rutaFilter,
  ]);

  // ============================================================
  // PAGINACIÓN
  // ============================================================

  const totalPages = Math.max(
    1,
    Math.ceil(
      filtered.length / perPage
    )
  );

  const paginatedData =
    filtered.slice(
      (page - 1) * perPage,
      page * perPage
    );

  // ============================================================
  // EXPORTAR EXCEL
  // ============================================================

  const exportToExcel = () => {
    const exportData =
      filtered.map((row) => {
        const obj = {
          ID: row.ID,
          Ruta: row.Ruta,
          Vuelo: row.Vuelo,
        };

        DAYS.forEach((dia) => {
          const equipo = row.dias ? row.dias[String(dia)] : null;
          obj[`Día ${dia}`] = equipo || 'No Opera';
        });

        return obj;
      });

    const ws =
      XLSX.utils.json_to_sheet(
        exportData
      );

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      'TD ITI'
    );

    XLSX.writeFile(
      wb,
      `TD_ITI_${anio}_${String(
        mes
      ).padStart(2, '0')}.xlsx`
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Flex
        justify="space-between"
        align="center"
        mb="md"
        wrap="wrap"
        gap="md"
      >
        <Text
          fw={700}
          size="xl"
          c="#020C41"
        >
          TD ITI — Días que No Opera
        </Text>

        {/* ====================================================
            SELECTOR MES / AÑO
            ==================================================== */}

        <Group
          gap="xs"
          align="center"
        >
          {/* Mes anterior */}

          <ActionIcon
            size="sm"
            variant="light"
            color="#030D75"
            onClick={prevMonth}
            title="Mes anterior"
          >
            <IconChevronLeft
              size={14}
            />
          </ActionIcon>

          {/* Mes */}

          <Select
            value={String(mes)}
            onChange={
              handleMonthChange
            }
            data={MESES}
            size="xs"
            allowDeselect={false}
            style={{
              width: 135,
            }}
          />

          {/* Año */}

          <Select
            value={String(anio)}
            onChange={
              handleYearChange
            }
            data={getYearOptions()}
            size="xs"
            allowDeselect={false}
            style={{
              width: 90,
            }}
          />

          {/* Mes siguiente */}

          <ActionIcon
            size="sm"
            variant="light"
            color="#030D75"
            onClick={nextMonth}
            title="Mes siguiente"
          >
            <IconChevronRight
              size={14}
            />
          </ActionIcon>
        </Group>

        {/* Última carga */}

        <Text
          size="xs"
          c="dimmed"
          fs="italic"
        >
          Última carga:{' '}
          {new Date().toLocaleString(
            'es-MX',
            {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }
          )}
        </Text>
      </Flex>

      {/* ======================================================
          FILTROS
      ====================================================== */}

      <Paper
        shadow="xs"
        p="md"
        mb="md"
        radius="md"
      >
        <Flex
          gap="md"
          align="end"
          wrap="wrap"
        >
          {/* Buscar */}

          <TextInput
            label="Buscar"
            placeholder="ID, Ruta o Vuelo..."
            leftSection={
              <IconSearch
                size={16}
              />
            }
            value={search}
            onChange={(e) => {
              setSearch(
                e.currentTarget.value
              );

              setPage(1);
            }}
            style={{
              flex: 1,
              minWidth: 200,
            }}
          />

          {/* Ruta */}

          <Select
            label="Ruta"
            placeholder="Todas"
            data={rutaOptions}
            value={rutaFilter}
            onChange={(value) => {
              setRutaFilter(value);
              setPage(1);
            }}
            clearable
            searchable
            leftSection={
              <IconFilter
                size={16}
              />
            }
            style={{
              minWidth: 200,
            }}
          />

          {/* Excel */}

          <Button
            size="sm"
            variant="light"
            color="#FA0073"
            leftSection={
              <IconDownload
                size={16}
              />
            }
            onClick={exportToExcel}
          >
            Excel
          </Button>
        </Flex>
      </Paper>

      {/* ======================================================
          ERROR
      ====================================================== */}

      {apiError && (
        <Paper
          shadow="xs"
          p="md"
          mb="md"
          radius="md"
        >
          <Text
            size="sm"
            c="red"
          >
            Error al cargar
            itinerario:{' '}
            {apiError}
          </Text>
        </Paper>
      )}

      {/* ======================================================
          TABLA
      ====================================================== */}

      <Paper
        shadow="xs"
        radius="md"
        style={{
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            overflowX: 'auto',
          }}
        >
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing={4}
            verticalSpacing="xs"
            layout="auto"
            style={{
              textAlign: 'center',
              fontSize:
                '0.75rem',
            }}
          >
            {/* ==================================================
                HEADER
                ================================================== */}

            <Table.Thead
              style={{
                backgroundColor:
                  '#030D75',
              }}
            >
              <Table.Tr>
                {/* ID */}

                <Table.Th
                  style={{
                    color: 'white',
                    whiteSpace:
                      'nowrap',
                    textAlign:
                      'center',
                    position:
                      'sticky',
                    left: 0,
                    backgroundColor:
                      '#030D75',
                    zIndex: 2,
                  }}
                >
                  ID
                </Table.Th>

                {/* Ruta */}

                <Table.Th
                  style={{
                    color: 'white',
                    whiteSpace:
                      'nowrap',
                    textAlign:
                      'center',
                  }}
                >
                  Ruta
                </Table.Th>

                {/* Vuelo */}

                <Table.Th
                  style={{
                    color: 'white',
                    whiteSpace:
                      'nowrap',
                    textAlign:
                      'center',
                  }}
                >
                  Vuelo
                </Table.Th>

                {/* Días */}

                {DAYS.map(
                  (dia, index) => (
                    <Table.Th
                      key={dia}
                      onMouseEnter={() => setHoveredCol(dia)}
                      onMouseLeave={() => setHoveredCol(null)}
                      style={{
                        color:
                          'white',
                        textAlign:
                          'center',
                        minWidth: 28,
                        padding:
                          '4px 2px',
                        backgroundColor: hoveredCol === dia ? 'rgba(3,13,117,0.25)' : undefined,
                      }}
                    >
                      {dia}
                      <br />

                      <span
                        style={{
                          opacity: 0.7,
                          fontSize:
                            '0.6rem',
                        }}
                      >
                        {
                          DAY_LETTERS[
                            index
                          ]
                        }
                      </span>
                    </Table.Th>
                  )
                )}
              </Table.Tr>
            </Table.Thead>

            {/* ==================================================
                BODY
                ================================================== */}

            <Table.Tbody>
              {/* Loading */}

              {loading ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={
                      DAYS.length + 3
                    }
                    style={{
                      textAlign:
                        'center',
                      padding: 30,
                    }}
                  >
                    <Text
                      size="sm"
                      c="dimmed"
                    >
                      Cargando
                      información...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : paginatedData.length ===
                0 ? (
                /* Sin información */

                <Table.Tr>
                  <Table.Td
                    colSpan={
                      DAYS.length + 3
                    }
                    style={{
                      textAlign:
                        'center',
                      padding: 30,
                    }}
                  >
                    <Text
                      size="sm"
                      c="dimmed"
                    >
                      No hay
                      información
                      para{' '}
                      {getMonthName(
                        mes
                      ).toUpperCase()}{' '}
                      {anio}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                /* Registros */

                paginatedData.map(
                  (row) => {
                    const diasNoOpera =
                      Array.isArray(
                        row.diasNoOpera
                      )
                        ? row.diasNoOpera
                        : [];

                    return (
                      <Table.Tr
                        key={`${row.ID}-${row.Vuelo}`}
                      >
                        {/* ID */}

                        <Table.Td
                          style={{
                            textAlign:
                              'center',
                            whiteSpace:
                              'nowrap',
                            position:
                              'sticky',
                            left: 0,
                            backgroundColor:
                              'inherit',
                            zIndex: 1,
                          }}
                        >
                          <Text
                            size="xs"
                            fw={500}
                            c="#020C41"
                          >
                            {row.ID}
                          </Text>
                        </Table.Td>

                        {/* Ruta */}

                        <Table.Td
                          style={{
                            textAlign:
                              'center',
                          }}
                        >
                          <Badge
                            variant="light"
                            color={
                              String(
                                row.Ruta ||
                                  ''
                              ).startsWith(
                                'MEX'
                              )
                                ? 'am-blue'
                                : 'am-pink'
                            }
                            size="xs"
                          >
                            {row.Ruta ||
                              '—'}
                          </Badge>
                        </Table.Td>

                        {/* Vuelo */}

                        <Table.Td
                          style={{
                            textAlign:
                              'center',
                          }}
                        >
                          <Text size="xs">
                            {row.Vuelo ??
                              '—'}
                          </Text>
                        </Table.Td>

                        {/* Días */}

                        {DAYS.map(
                          (dia) => {
                            const equipo = row.dias ? row.dias[String(dia)] : null;
                            const noOpera = !equipo;
                            const isHovered = hoveredCol === dia;

                            return (
                              <Table.Td
                                key={
                                  dia
                                }
                                onMouseEnter={() => setHoveredCol(dia)}
                                onMouseLeave={() => setHoveredCol(null)}
                                style={{
                                  textAlign:
                                    'center',
                                  padding:
                                    '4px 2px',
                                  backgroundColor:
                                    isHovered
                                      ? 'rgba(3,13,117,0.08)'
                                      : noOpera
                                        ? 'rgba(250,0,115,0.15)'
                                        : 'rgba(46,125,50,0.06)',
                                }}
                              >
                                {noOpera ? (
                                  <Text
                                    size="xs"
                                    fw={700}
                                    c="#FA0073"
                                  >
                                    ✗
                                  </Text>
                                ) : (
                                  <Text
                                    size="xs"
                                    fw={600}
                                    c="#020C41"
                                  >
                                    {equipo}
                                  </Text>
                                )}
                              </Table.Td>
                            );
                          }
                        )}
                      </Table.Tr>
                    );
                  }
                )
              )}
            </Table.Tbody>
          </Table>
        </Box>

        {/* ====================================================
            PAGINACIÓN
            ==================================================== */}

        <Flex
          justify="space-between"
          align="center"
          p="md"
        >
          <Text
            size="sm"
            c="dimmed"
          >
            {filtered.length ===
            0
              ? '0 registros'
              : `Mostrando ${
                  (page - 1) *
                    perPage +
                  1
                }–${Math.min(
                  page * perPage,
                  filtered.length
                )} de ${
                  filtered.length
                }`}
          </Text>

          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            color="am-blue"
            size="sm"
          />
        </Flex>
      </Paper>
    </>
  );
}